import { createAreaEffectShape } from './area_effect_shape.js';
import { SHAPE_DEFAULTS } from './constants.js';
import {
  areaEffectAtPointer,
  areaEffectGeometry,
  defaultEffectName,
  isDirectionalEffect,
  validAreaEffect
} from './area_effect_geometry.js';

export const AreaEffectMethods = {
  _areaEffectGrid() {
    return { size: this.gridSizeValue, offsetX: this.gridOffsetXValue, offsetY: this.gridOffsetYValue };
  },

  confirmAreaEffect(event) {
    if (this.readonlyValue || !this._stage) return;
    const effect = event.detail;
    if (!validAreaEffect(effect) || !/^#[0-9a-f]{6}$/i.test(effect.color)) return;
    this._showToolbar('shapes', 'select');
    this._pendingAreaEffect = { ...effect, name: effect.name?.trim() || defaultEffectName(effect) };
    if (effect.parentId) {
      const parent = this._findShapeById(effect.parentId);
      if (!this._canEditShape(parent) || parent.type !== 'character') return;
      this._placeAreaEffect();
      this._showEditModal(parent.id, { tab: 'effects' });
      return;
    }
    this._applyTool('area-effect');
    if (this._mobileEditor) this.collapseMapControls();
    window.modal?.hideModal();
  },

  _clearAreaEffectPreview() {
    this._areaEffectPreview?.destroy();
    this._areaEffectPreview = null;
    this._cursorLayer?.batchDraw();
  },

  _previewAreaEffect(point = null) {
    if (!this._pendingAreaEffect || !this._cursorLayer) return;
    point ||= this._getWorldPointerPosition();
    const effect = areaEffectAtPointer(point, this._pendingAreaEffect, this._areaEffectGrid());
    const geometry = effect && areaEffectGeometry(effect, this._areaEffectGrid());
    if (!geometry) return;
    this._areaEffectPlacementPoint = point;
    this._syncMobileControls?.();
    if (!this._areaEffectPreview) {
      this._areaEffectPreview = createAreaEffectShape(effect, {
        ...geometry,
        stroke: this._pendingAreaEffect.color,
        strokeWidth: SHAPE_DEFAULTS.strokeWidth,
        strokeScaleEnabled: false,
        fill: this._hexToRgba(this._pendingAreaEffect.color, SHAPE_DEFAULTS.fillOpacity),
        dash: [8, 4],
        listening: false
      });
      this._cursorLayer.add(this._areaEffectPreview);
    }
    this._areaEffectPreview.setAttrs({ ...geometry, visible: true });
    this._cursorLayer.moveToTop();
    this._cursorLayer.batchDraw();
  },

  _placeAreaEffect(point = null) {
    if (this.readonlyValue || !this._pendingAreaEffect) return;
    point ||= this._getWorldPointerPosition();
    const parent = this._pendingAreaEffect.parentId && this._findShapeById(this._pendingAreaEffect.parentId);
    const effect = parent
      ? { ...this._pendingAreaEffect, gridColumn: parent.gridColumn, gridRow: parent.gridRow }
      : areaEffectAtPointer(point, this._pendingAreaEffect, this._areaEffectGrid());
    const geometry = effect && areaEffectGeometry(effect, this._areaEffectGrid(), parent);
    if (!geometry) return;
    const color = this._pendingAreaEffect.color;
    const shape = {
      ...SHAPE_DEFAULTS,
      ...effect,
      id: `shape_${Date.now()}_${++this._shapeCounter}`,
      name: this._pendingAreaEffect.name,
      type: 'area-effect',
      creatorId: this.userIdValue,
      color,
      fillColor: color,
      showLabels: true,
      konvaShape: createAreaEffectShape(effect, {
        ...geometry,
        stroke: color,
        strokeWidth: SHAPE_DEFAULTS.strokeWidth,
        strokeScaleEnabled: false,
        fill: this._hexToRgba(color, SHAPE_DEFAULTS.fillOpacity),
        listening: false
      })
    };
    if (parent) this._shapes.splice(this._shapes.indexOf(parent), 0, shape);
    else this._shapes.push(shape);
    this._getShapeLayer(shape).add(shape.konvaShape);
    this._recreateLabels(shape);
    this._applyTool('select');
    this._selectShape(shape.id);
    this._rebuildShapeZOrder();
    this._save();
  },

  _moveAreaEffect(shape, position) {
    if (shape.parentId) return;
    const origin = isDirectionalEffect(shape)
      ? position
      : {
          x: position.x + shape.konvaShape.width() / 2,
          y: position.y + shape.konvaShape.height() / 2
        };
    const effect = areaEffectAtPointer(origin, shape, this._areaEffectGrid());
    if (!effect) return;
    Object.assign(shape, effect);
    shape.konvaShape.setAttrs(areaEffectGeometry(shape, this._areaEffectGrid(), this._findShapeById(shape.parentId)));
    this._syncEffectRotationHandle();
  },

  changeAreaEffectSize(event) {
    const shape = this._findShapeById(event.currentTarget.dataset.shapeId);
    if (!shape || !['radius', 'cube'].includes(shape.effectType)) return;
    const property = shape.effectType === 'cube' ? 'sideFeet' : 'radiusFeet';
    const feet = Number(event.currentTarget.value);
    const effect = { ...shape, [property]: feet };
    if (!validAreaEffect(effect)) return;
    if (shape.name === defaultEffectName(shape)) shape.name = defaultEffectName(effect);
    shape[property] = feet;
    this._syncAreaEffectGeometry();
    this._updateShapesList();
    this._refreshEditModal();
    this._save();
  },

  changeDirectionalEffect(event) {
    const shape = this._findShapeById(event.currentTarget.dataset.shapeId);
    if (!isDirectionalEffect(shape)) return;
    const field = event.currentTarget.dataset.effectProperty;
    if (!['lengthFeet', 'widthFeet', 'directionDegrees'].includes(field)) return;
    if (field === 'widthFeet' && shape.effectType !== 'line') return;
    const raw = event.currentTarget.value;
    if (raw === '') return;
    const value = Number(raw);
    const effect = { ...shape, [field]: value };
    if (!validAreaEffect(effect)) return;
    if (shape.name === defaultEffectName(shape)) shape.name = defaultEffectName(effect);
    shape[field] = value;
    this._syncAreaEffectGeometry();
    this._updateShapesList();
    this._refreshEditModal();
    this._save();
  },

  _syncLinkedEffects(parentId) {
    for (const effect of this._shapes.filter((shape) => shape.parentId === parentId)) {
      const parent = this._findShapeById(parentId);
      const geometry = areaEffectGeometry(effect, this._areaEffectGrid(), parent);
      if (!geometry) continue;
      effect.gridColumn = parent.gridColumn;
      effect.gridRow = parent.gridRow;
      effect.konvaShape.setAttrs(geometry);
      this._recreateLabels(effect);
    }
    this._shapesLayerAbove?.batchDraw();
    this._shapesLayerBelow?.batchDraw();
  },

  _syncAreaEffectGeometry() {
    this._finishShapeAnimations?.();
    this._syncCharacterTokenGeometry?.();
    for (const shape of this._shapes || []) {
      if (shape.type !== 'area-effect') continue;
      const geometry = areaEffectGeometry(shape, this._areaEffectGrid(), this._findShapeById(shape.parentId));
      if (!geometry) continue;
      shape.konvaShape.setAttrs({ ...geometry, scaleX: 1, scaleY: 1 });
      this._recreateLabels(shape);
    }
    this._shapeTransformer?.forceUpdate();
    this._syncEffectRotationHandle();
    this._shapesLayerAbove?.batchDraw();
    this._shapesLayerBelow?.batchDraw();
    if (this._pendingAreaEffect) this._previewAreaEffect();
  }
};
